import useEventListener from "@pathwright/ui/src/components/hooks/useEventListener"
import usePath from "@pathwright/features/src/modules/path/hooks/usePath"
import { ORDER_FULFILLED_EVENT } from "@pathwright/features/src/modules/utils/dispatcher"
import get from "lodash/get"
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react"
import useStores from "../../../../../lib/core/store/components/useStores"
import { serializePathForStore } from "./utils"

export const PathContext = React.createContext()

export const usePathProvider = () => React.useContext(PathContext)

const stores = [
  {
    store: "path",
    watch: false
  },
  {
    store: "step",
    watch: false
  },
  {
    store: "auth",
    watch: ["is_authenticated"]
  },
  {
    store: "resource",
    watch: ["resource", "offering"]
  }
]

function usePathProviderState() {
  const state = useStores(stores)

  const { resource, offering } = state.resourceStore.state
  const { path: pathStorePath } = state.pathStore.state
  const { setPath } = state.pathStore.action
  const { user } = state.authStore.state

  const pathSerializationData = useMemo(
    () => ({
      resource,
      offering,
      user
    }),
    [resource, offering, user]
  )

  return {
    setPath,
    pathStorePath,
    pathSerializationData
  }
}

// handles setting path, step, and lesson in path and step stores based on props
const PathProvider = ({ children }) => {
  const { setPath, pathStorePath, pathSerializationData } =
    usePathProviderState()

  const currentPathRef = useRef(null)
  const { offering } = pathSerializationData
  const [isDragging, setIsDragging] = useState(false)

  const [
    { pathId, cohortId, userId, forceReload, ensureConsistentRead },
    loadPath
  ] = useReducer(
    (
      state,
      {
        pathId,
        cohortId,
        userId,
        forceReload = false,
        ensureConsistentRead = false
      }
    ) => {
      if (
        state.pathId !== pathId ||
        state.cohortId !== cohortId ||
        state.userId !== userId
      ) {
        // determine whether to load from network to avoid a stale cache
        const nextForceReload =
          forceReload ||
          (pathId
            ? // pathId differs from pathStorePath ?
              pathId !== get(pathStorePath, "id", null)
            : cohortId
            ? // cohortId differs from pathStorePath ?
              cohortId !== get(pathStorePath, "offering_id", null) ||
              pathId !== state.pathId // in this case, the cohort is the same but the pathId may differ from state ?
            : userId
            ? // userId differs from pathStorePath ?
              userId !== get(pathStorePath, "user", null)
            : false)

        return {
          pathId,
          cohortId,
          userId,
          forceReload: nextForceReload,
          ensureConsistentRead
        }
      }

      if (
        state.forceReload === forceReload &&
        state.ensureConsistentRead === ensureConsistentRead
      ) {
        // Since next state has not changed from current state, return current state
        // and React will detect that state has not changed and will not trigger rerenders.
        return state
      }

      return {
        ...state,
        forceReload,
        ensureConsistentRead
      }
    },
    // initial state
    {
      pathId: null,
      cohortId: null,
      userId: null,
      forceReload: false,
      ensureConsistentRead: false
    }
  )

  const { path, error, loaded, loading, syncing, syncPath, refetch } = usePath({
    pathId,
    cohortId,
    userId,
    forceReload,
    ensureConsistentRead
  })

  useEffect(() => {
    if (loaded && (forceReload || ensureConsistentRead)) {
      // hacky way of resetting forceReload and ensureConsistentRead
      loadPath({ pathId, cohortId, userId })
    }
  }, [loaded])

  useEffect(() => {
    // clearing the path ref
    if (!pathId && !cohortId && !userId) {
      currentPathRef.current = null
    }
  }, [pathId, cohortId, userId])

  // let's not consider the path "loaded" until the correct offering has been loaded
  let offeringLoaded = offering && offering.id === cohortId
  const pathLoaded = loaded && offeringLoaded && !forceReload

  const shouldSyncPathStore =
    pathLoaded &&
    // Require path data to have changed.
    (currentPathRef.current !== path || path?.id !== pathStorePath?.id) &&
    // Require loader vars to be consistent with the path data.
    (!userId || path?.user?.id === userId) &&
    (!pathId || path?.id === pathId)

  const syncPathStore = useCallback(() => {
    currentPathRef.current = path
    const serializedPath = serializePathForStore({
      path,
      ...pathSerializationData
    })
    // set the path in the path store
    setPath(serializedPath, true /* forceUpdate */) // always force update
    if (isDragging) {
      setIsDragging(false)
    }
  }, [isDragging, path, pathSerializationData, setPath])

  // Drag/drop useLayoutEffect syncs the path store before paint to prevent a transient frame
  // where the list appears to "snap back" to pre-drop ordering before the path store is synced.
  // This also prevents brief display of stale data just after optimistic update but before the
  // path store is synced.
  useLayoutEffect(() => {
    if (shouldSyncPathStore) {
      syncPathStore()
    }
  }, [shouldSyncPathStore, syncPathStore])

  // Force reloading default path for the user to ensure
  // that an already loaded default path is replaced with the user's own path
  // though the default path will still be loaded for admins / teachers but that should be fine.
  useEventListener(
    ORDER_FULFILLED_EVENT,
    (e) => {
      const { order } = e.detail

      if (loaded && order.order_type === "registration") {
        loadPath({
          pathId: null,
          cohortId: order.order_data.offering_id,
          forceReload: true,
          ensureConsistentRead: true
        })
      }
    },
    [loaded]
  )

  return (
    <PathContext.Provider
      value={{
        path,
        error,
        loaded: pathLoaded,
        loading,
        syncing,
        syncPath,
        refetch,
        pathId,
        cohortId,
        userId,
        loadPath,
        isDragging,
        setIsDragging
      }}
    >
      {children}
    </PathContext.Provider>
  )
}

PathProvider.displayName = "PathProvider"

export const withPathProvider = (Component) => (props) =>
  (
    <PathProvider>
      <Component {...props} />
    </PathProvider>
  )

export default PathProvider
